/**
 * "Linked publishing posts" — the read-only list that appears inside an event
 * record and inside an ambassador record.
 *
 * One component rather than two, because the design draws them identically:
 * date · channels on the left, the post's status on the right, and the whole
 * block hidden when there is nothing to show. The link is owned by the post, so
 * this end of it is deliberately not editable — you change it in the post.
 *
 * The query runs on open rather than through a live subscription. These lists
 * are a handful of rows read while a modal is up; a snapshot listener per open
 * record would cost more than it is worth.
 */
import { useEffect, useState } from 'react'
import { postsForAmbassador, postsForEvent } from './repository/postsRepository'
import { postStatusStyle, type Post } from '@/types/posts'
import { formatDate } from '@/shared/util/dates'
import './LinkedPosts.css'

interface Props {
  /** Exactly one of these. An empty id means an unsaved record — nothing to look up. */
  eventId?: string
  ambassadorId?: string
}

export function LinkedPosts({ eventId, ambassadorId }: Props) {
  const [posts, setPosts] = useState<Post[]>([])

  useEffect(() => {
    let live = true
    const id = eventId ?? ambassadorId ?? ''
    if (!id) {
      setPosts([])
      return
    }
    const load = eventId ? postsForEvent(eventId) : postsForAmbassador(ambassadorId!)
    load
      .then((rows) => {
        if (!live) return
        // Newest first, and unscheduled posts last — an empty date sorts after
        // any real one rather than to the top as a blank string would.
        rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        setPosts(rows)
      })
      // A failed read here should not take the record editor down with it. The
      // block simply stays hidden, which is what it does when there are no posts.
      .catch(() => {
        if (live) setPosts([])
      })
    return () => {
      live = false
    }
  }, [eventId, ambassadorId])

  if (posts.length === 0) return null

  return (
    <div className="linkedposts">
      <label className="linkedposts__label">Linked publishing posts</label>
      <div className="linkedposts__list">
        {posts.map((p) => {
          const st = postStatusStyle(p.status)
          return (
            <div className="linkedposts__row" key={p.id}>
              <span className="linkedposts__what">
                {p.date ? formatDate(p.date) : 'Unscheduled'} · {p.channels.join(', ') || 'no channel'}
              </span>
              <span className="pill" style={{ background: st.bg, color: st.color }}>
                {st.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
